<script setup>
import { computed, ref } from 'vue';
import { formatMoney, formatNumber, formatSignedMoney } from '../format';
import { t } from '../i18n';
import { categoryIcon, fullDateTime, timeLabel, typeLabel } from '../operations';

// One operation (docs/operations/DESIGN.md, «Строка операции»): two lines and a tap-to-open block on a
// phone, a grid row on a desktop. Sums are white: money moving is not profit (design decision 2).
const props = defineProps({
  op: {
    type: Object,
    required: true,
  },
  desktop: {
    type: Boolean,
    default: false,
  },
  // A portfolio of several accounts shows which one.
  showAccount: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);

const logoBroken = ref(false);

// Securities transfers in (17) and out (3) carry no money: the quantity shows the direction.
const SECURITIES_IN = 17;
const SECURITIES_OUT = 3;

const type = computed(() => typeLabel(props.op));
const instrument = computed(() => props.op.instrument);
const title = computed(() => instrument.value?.name || instrument.value?.ticker || type.value);

const qtyPrice = computed(() => {
  const { quantity, price, price_currency: currency } = props.op;
  if (!quantity) {
    return '';
  }
  if (price === null || price === undefined) {
    return t('operations.qty', { qty: formatNumber(quantity) });
  }
  return t('operations.qtyPrice', { qty: formatNumber(quantity), price: formatMoney(price, currency) });
});

// Second line, left: what kind of operation and its details.
const detail = computed(() => {
  if (!instrument.value) {
    return '';
  }
  if (props.op.category === 'trades' && qtyPrice.value) {
    return `${type.value} · ${qtyPrice.value}`;
  }
  if (props.op.category === 'repayments' && props.op.quantity) {
    return `${type.value} · ${t('operations.qty', { qty: formatNumber(props.op.quantity) })}`;
  }
  return type.value;
});

const amount = computed(() => {
  const { payment, currency, quantity } = props.op;
  if (payment !== null && payment !== undefined) {
    return { text: payment === 0 ? formatMoney(0, currency) : formatSignedMoney(payment, currency), muted: payment === 0 };
  }
  if (quantity) {
    const sign = props.op.type === SECURITIES_IN ? '+' : props.op.type === SECURITIES_OUT ? '−' : '';
    return { text: `${sign}${t('operations.qty', { qty: formatNumber(quantity) })}`, muted: false, quantity: true };
  }
  return { text: '—', muted: true };
});

const accrued = computed(() => (props.op.accrued_int ? t('operations.accrued', { amount: formatMoney(props.op.accrued_int, props.op.accrued_currency) }) : ''));

// The broker's own text, when it says more than our short name.
const brokerText = computed(() => {
  const text = props.op.description;
  return text && text !== type.value && text !== title.value ? text : '';
});

const initial = computed(() => (instrument.value?.ticker || instrument.value?.name || '?').slice(0, 1).toUpperCase());
</script>

<template>
  <!-- Desktop: one grid row, nothing to open (DESIGN.md, «Десктоп») -->
  <div
    v-if="desktop"
    class="grid items-center gap-3 px-5 py-2 text-sm hover:bg-slate-800/40"
    :class="showAccount ? 'grid-cols-[3rem_minmax(0,2fr)_minmax(0,1.6fr)_minmax(0,1fr)_9rem]' : 'grid-cols-[3rem_minmax(0,2fr)_minmax(0,1.6fr)_9rem]'"
    :title="brokerText || undefined"
  >
    <span class="tabular-nums text-slate-500">{{ timeLabel(op.executed_at) }}</span>
    <span class="flex min-w-0 items-center gap-2">
      <img
        v-if="instrument?.logo_url && !logoBroken"
        :src="instrument.logo_url"
        alt=""
        loading="lazy"
        class="size-7 shrink-0 rounded-full bg-slate-800 object-cover"
        @error="logoBroken = true"
      >
      <span
        v-else-if="instrument"
        class="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-semibold text-white"
        :style="instrument.logo_color ? { backgroundColor: instrument.logo_color } : null"
        aria-hidden="true"
      >{{ initial }}</span>
      <span
        v-else
        class="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-800 text-slate-300"
        aria-hidden="true"
      >
        <svg
          class="size-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        ><path :d="categoryIcon(op.category)" /></svg>
      </span>
      <span class="min-w-0 truncate text-slate-100">{{ title }}</span>
      <span
        v-if="instrument?.ticker && instrument.name"
        class="shrink-0 text-xs text-slate-500"
      >{{ instrument.ticker }}</span>
    </span>
    <span class="min-w-0 truncate text-slate-300">
      <template v-if="instrument">{{ detail }}</template>
      <template v-if="accrued"> · {{ accrued }}</template>
    </span>
    <span
      v-if="showAccount"
      class="min-w-0 truncate text-slate-400"
    >{{ op.portfolio_name }}</span>
    <span
      class="text-right tabular-nums"
      :class="amount.muted ? 'text-slate-500' : amount.quantity ? 'text-slate-300' : 'text-slate-100'"
    >{{ amount.text }}</span>
  </div>

  <!-- Phone: two lines, details on tap -->
  <button
    v-else
    type="button"
    class="flex w-full items-start gap-3 px-5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
    :aria-expanded="expanded"
    @click="emit('toggle')"
  >
    <img
      v-if="instrument?.logo_url && !logoBroken"
      :src="instrument.logo_url"
      alt=""
      loading="lazy"
      class="size-10 shrink-0 rounded-full bg-slate-800 object-cover"
      @error="logoBroken = true"
    >
    <span
      v-else-if="instrument"
      class="flex size-10 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[0.8125rem] font-semibold text-white"
      :style="instrument.logo_color ? { backgroundColor: instrument.logo_color } : null"
      aria-hidden="true"
    >{{ initial }}</span>
    <span
      v-else
      class="flex size-10 shrink-0 items-center justify-center rounded-full bg-slate-800 text-slate-300"
      aria-hidden="true"
    >
      <svg
        class="size-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.75"
        stroke-linecap="round"
        stroke-linejoin="round"
      ><path :d="categoryIcon(op.category)" /></svg>
    </span>
    <span class="min-w-0 flex-1">
      <span class="flex items-baseline justify-between gap-3">
        <span class="min-w-0 truncate text-[0.9375rem] font-medium leading-5 text-slate-100">{{ title }}</span>
        <span
          class="shrink-0 text-[0.9375rem] font-medium leading-5 tabular-nums"
          :class="amount.muted ? 'text-slate-500' : amount.quantity ? 'text-slate-300' : 'text-slate-100'"
        >{{ amount.text }}</span>
      </span>
      <span class="mt-1 flex items-baseline justify-between gap-3 text-[0.8125rem] leading-4">
        <span class="min-w-0 truncate text-slate-400">{{ detail }}</span>
        <span class="shrink-0 tabular-nums text-slate-500">{{ timeLabel(op.executed_at) }}</span>
      </span>
      <span
        v-if="expanded"
        class="mt-2 block space-y-0.5 text-[0.8125rem] leading-5 tabular-nums text-slate-400"
      >
        <span class="block">{{ fullDateTime(op.executed_at) }}</span>
        <span
          v-if="showAccount && op.portfolio_name"
          class="block"
        >{{ op.portfolio_name }}</span>
        <span
          v-if="instrument && (qtyPrice || accrued)"
          class="block"
        >{{ [qtyPrice, accrued].filter(Boolean).join(' · ') }}</span>
        <span
          v-if="instrument?.ticker && instrument.name"
          class="block"
        >{{ instrument.ticker }}</span>
        <span
          v-if="brokerText"
          class="block text-slate-500"
        >{{ brokerText }}</span>
      </span>
    </span>
  </button>
</template>
